import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Factory, ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockedMsg, setLockedMsg] = useState("");   // shows contact-admin popup
  const [isAdminEmail, setIsAdminEmail] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Debounced admin-email check — only surface the "Forgot password?" link when
  // the typed email belongs to an active admin.
  useEffect(() => {
    setForgotSent(false);
    const trimmed = email.trim().toLowerCase();
    // basic client-side email shape — skip until it looks real
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setIsAdminEmail(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/auth/check-admin-email", { email: trimmed });
        if (!cancelled) setIsAdminEmail(!!r.data?.is_admin);
      } catch { if (!cancelled) setIsAdminEmail(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email]);

  if (user) return <Navigate to="/dashboard" replace />;

  const sendReset = async () => {
    if (!email.trim()) return;
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setForgotSent(true);
      toast.success("Reset link sent — check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset link");
    } finally {
      setForgotBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Login failed";
      if (status === 423) {
        setLockedMsg(detail);   // dedicated popup, not just a toast
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-zinc-100">
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1651525670114-2b8117390b28?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt="Factory"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo/eaths-logo.webp" alt="Eath's" className="w-12 h-12 object-contain bg-white p-1 rounded" />
              <div>
                <div className="brand-font text-xl text-orange-400 leading-none normal-case">Eath's <span className="font-mono text-xs text-orange-400/70 tracking-[0.3em] align-middle">/ CRM</span></div>
                <div className="font-display font-black text-lg leading-none">MANUFACTURING OS</div>
              </div>
            </div>
            <Link to="/" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-orange-400 transition-colors" data-testid="back-to-landing">
              <ArrowLeft size={12} weight="bold" /> Back to site
            </Link>
          </div>
          <div className="max-w-lg">
            <div className="kr-label text-orange-400 mb-4">CONTROL ROOM v1.0</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black uppercase leading-[0.92] tracking-tighter">
              Run the floor.<br/>
              <span className="text-orange-500">Hit the goal.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-md leading-relaxed">
              Inventory, batches, attendance, GST invoicing & accounting —
              one industrial dashboard for your production floor.
            </p>
          </div>
          <div className="font-mono text-xs text-zinc-400">
            FIRST-IN / FIRST-OUT · CODE128 BARCODES · ROLE-GATED ACCESS
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-600 flex items-center justify-center text-white">
              <Factory size={20} weight="fill" />
            </div>
            <div className="font-display font-black text-lg">KR CRM</div>
          </div>
          <div className="kr-label mb-2 flex items-center justify-between">
            <span>Sign in</span>
            <Link to="/" className="md:hidden inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-orange-600" data-testid="back-to-landing-mobile">
              <ArrowLeft size={10} weight="bold" /> Site
            </Link>
          </div>
          <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-1">Operator Login</h2>
          <p className="text-sm text-zinc-500 mb-8">Enter your credentials to access the control room.</p>

          <label className="block kr-label mb-1.5">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            placeholder="Your Email"
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
            required
          />
          <label className="block kr-label mt-5 mb-1.5">Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
            required
          />

          <button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="kr-btn-primary w-full mt-8 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock size={16} weight="bold" />
            {busy ? "Authenticating…" : "Enter Control Room"}
          </button>
          {isAdminEmail && (
            <div className="mt-4 text-center" data-testid="admin-forgot-block">
              {forgotSent ? (
                <div className="text-xs font-mono text-green-700" data-testid="reset-sent-msg">
                  ✓ Reset link sent to <span className="font-bold">{email}</span> · valid 15 min
                </div>
              ) : (
                <button
                  type="button"
                  disabled={forgotBusy}
                  onClick={sendReset}
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-orange-600 disabled:opacity-50 transition-colors"
                  data-testid="forgot-password-link"
                >
                  <PaperPlaneTilt size={12} weight="bold" />
                  {forgotBusy ? "Sending reset link…" : "Forgot password? Send reset link →"}
                </button>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Account-locked popup — shown when backend returns HTTP 423. */}
      {lockedMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="locked-modal">
          <div className="bg-white border-2 border-red-600 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-600 flex items-center justify-center text-white">
                <Lock size={20} weight="fill" />
              </div>
              <div>
                <div className="kr-label text-red-700">Account Locked</div>
                <div className="font-display font-black text-lg uppercase tracking-tight">Access Denied</div>
              </div>
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed">{lockedMsg}</p>
            <p className="text-xs text-zinc-500 mt-3 font-mono">
              An admin must unlock your account manually from Settings → Users.
            </p>
            <button
              onClick={() => setLockedMsg("")}
              className="kr-btn-primary w-full mt-6 py-2.5 text-sm"
              data-testid="locked-modal-close"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
